/* eslint-disable import/no-duplicates */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { format, parseISO } from 'date-fns';

import { Container, Button, Row, Col } from 'react-bootstrap';

import api from '../../../services/api';

import {
  TIPO_USUARIO,
  formataTelefone,
  formataIdade,
} from '../../../utils/funcoes';

import { useToast } from '../../../hooks/toast';
import { useAuth } from '../../../hooks/auth';

import Select from '../../../components/Select';
import Loading from '../../../components/Loading';
import Paginacao from '../../../components/Paginacao';

import ItemTabela from './ItemTabela';

import getValidationErrors from '../../../utils/getValidationErrors';
import { IReturnInstanceInfo } from '../../Whatsapp';

export interface IPaciente {
  PacCodigo: number;
  PacNome: string;
  PacSexo: string;
  PacDataNasc: Date;
  PacDataNascFormatado: string;
  idade: string;
  PacTelefone: string;
  PacCelular: string;
  telefoneFormatado: string;
  PacEmail: string;
  PacFoto: string;
  dataUltimaConsulta?: Date;
  dataUltimaConsultaFormatado: string;
  textoMensagem: string;
  RegCodigo?: number;
  dataRegistro: Date;
  conferido: boolean;
  avatar_url?: string;
}

interface IFormBusca {
  termoBusca: string;
}

interface IPaginacao {
  termoBusca: string;
  limite: number;
  pagina: number;
}

const optionsBusca = [
  { value: 'hoje', label: 'Hoje' },
  { value: 'mes', label: 'Mês' },
];

const Aniversariantes: React.FC = () => {
  const history = useHistory();
  const { addToast } = useToast();
  const { user } = useAuth();

  const formRefBusca = useRef<FormHandles>(null);
  const [loading, setLoading] = useState(false);
  const [dadosRelatorio, setDadosRelatorio] = useState<IPaciente[]>();
  const [paginacao, setPaginacao] = useState<IPaginacao>({
    termoBusca: 'hoje',
    pagina: 1,
    limite: 20,
  });
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );

  useEffect(() => {
    if (user.UsuTipo === TIPO_USUARIO.MEDICO) {
      history.push('/sempermissao');
    }
  }, [history, user.UsuTipo]);

  const [instanceInfo, setInstanceInfo] = useState<IReturnInstanceInfo>({
    error: true,
    message: 'não conectado',
  });

  useEffect(() => {
    async function loadDadosInstancia(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IReturnInstanceInfo>('whatsapp/status');
        setInstanceInfo(response.data);
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    }
    loadDadosInstancia();
  }, []);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (!paginacao) return;
        const response = await api.get(
          `/pacientes/aniversariantes?termoBusca=${paginacao.termoBusca}&pagina=${paginacao.pagina}&limite=${paginacao.limite}`,
        );

        const pacientesApi: IPaciente[] = response.data[0];

        setTotalRegistros(
          response.data[1] === 0 ? undefined : response.data[1],
        );

        const dadosFormatados = pacientesApi.map(pac => {
          const dataNasc =
            pac.PacDataNasc && pac.PacDataNasc.toString().length > 9
              ? parseISO(pac.PacDataNasc.toString())
              : new Date();

          const telefoneFormatado =
            pac.PacTelefone && pac.PacTelefone.length > 0
              ? `${formataTelefone(pac.PacCelular)} - ${formataTelefone(
                  pac.PacTelefone,
                )}`
              : formataTelefone(pac.PacCelular);

          return {
            ...pac,
            PacDataNascFormatado: format(dataNasc, 'dd/MM/yyyy'),
            dataUltimaConsultaFormatado: pac.dataUltimaConsulta
              ? format(pac.dataUltimaConsulta, 'dd/MM/yyyy')
              : '',
            telefoneFormatado,
            idade: formataIdade(dataNasc, false),
            avatar_url: pac.avatar_url ? pac.avatar_url : '',
          };
        });

        setDadosRelatorio(dadosFormatados);
      } catch (error) {
        console.log(error);
        addToast({
          type: 'error',
          title: 'Não foi possível buscar os dados',
        });
      } finally {
        setLoading(false);
      }
    }

    if (paginacao) loadDados();
  }, [addToast, paginacao]);

  const handleSubmitBusca = useCallback(
    async (dados: IFormBusca) => {
      setLoading(true);
      try {
        formRefBusca.current?.setErrors({});
        const schema = Yup.object().shape({
          termoBusca: Yup.string().required(),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        setPaginacao({
          termoBusca: dados.termoBusca,
          pagina: 1,
          limite: 20,
        });
      } catch (error) {
        console.log(error);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);
          console.log('erros', errors);
          formRefBusca.current?.setErrors(errors);
        } else {
          addToast({ type: 'error', title: 'Erro ao buscar dados' });
        }
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  return (
    <Container fluid className="mt-2">
      {loading && <Loading />}
      <>
        <div className="d-print-none bg-white border p-2 mb-2">
          <div className="p-2 mb-2">
            <h2 id="topoBusca">Pacientes Aniversariantes</h2>
          </div>

          <Form ref={formRefBusca} onSubmit={handleSubmitBusca}>
            <Row>
              <Col md={2}>
                <Select name="termoBusca" label="" options={optionsBusca} />
              </Col>
              <Col md={2}>
                <Button type="submit" size="sm" variant="outline-secondary">
                  BUSCAR
                </Button>
              </Col>
            </Row>
          </Form>
        </div>

        <div className="bg-white border p-2">
          {dadosRelatorio && (
            <>
              <b>{totalRegistros} aniversariantes</b>
              <table
                className="table table-hover table-sm table-font-small table-bordered"
                style={{ borderWidth: '0px' }}
              >
                <thead>
                  <tr className="text-center">
                    <th>Pacientes</th>
                    <th style={{ minWidth: '100px' }}>Data Nasc.</th>
                    <th style={{ minWidth: '80px' }}>Idade</th>
                    <th style={{ minWidth: '100px' }}>Ult. Consulta</th>
                    <th style={{ minWidth: '120px' }}>Telefone</th>
                    <th style={{ minWidth: '120px' }}>E-mail</th>
                    <th style={{ minWidth: '30px' }}>-</th>
                    <th style={{ minWidth: '30px' }}>-</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosRelatorio &&
                    dadosRelatorio.map(rel => (
                      <ItemTabela
                        key={`des-${rel.PacCodigo}`}
                        paciente={rel}
                        conectado={
                          instanceInfo.instance_data?.phone_connected || false
                        }
                      />
                    ))}
                </tbody>
              </table>
            </>
          )}
          <Paginacao
            limitePagina={paginacao?.limite || 20}
            paginasVizinhas={2}
            totalRegistros={totalRegistros}
            paginaInicio={paginacao?.pagina}
            handleProximaPagina={handleProximaPagina}
          />
        </div>
      </>
    </Container>
  );
};

export default Aniversariantes;
